import * as _ from "lodash";

import { IPrice } from "src/core/shared/api/price-change.api";
import { IOrderLine } from "src/core/orders/api/orders.api";
import { IListLine } from "src/core/lists/api/lists.api";

/**
 * Formats the given number to a price to the minimum of 2 decimal places and
 * a maximum of 3 decimal places. Rounds to the nearest number.
 *
 * For example,
 *
 *   2 > 2.00
 *   2.12 > 2.12
 *   2.1234321 > 2.123
 *
 * 1) Converts the price into a string.
 * 2) Finds any decimal please and then splits the string.
 * 3) Counts how many units are after decimal point.
 * 4) Formats accordingly.
 *
 * @param price - The number to be formatted
 * @param fractionDigits - The number of digits after the decimal point. Defaults to 2.
 * @param currency - ISO 4217 currency code
 * @return A string over a number (parseFloat) as JS removed trailing zeros off of the number.
 */
export const formatPrice = (price: string | number | null, fractionDigits?: 2 | 3, currency = "GBP", notation?: Intl.NumberFormatOptions["notation"]): string => {
  let priceNumber = 0;
  //some languages format prices differntly, this gets the language from the browser
  const locale = navigator.language;

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });

  if (!price) {
    return formatter.format(priceNumber);
  }

  if (typeof price === "string") {
    priceNumber = parseFloat(price);
  } else {
    priceNumber = price;
  }

  return formatter.format(priceNumber);
};

/**
 * Customer toFixed function
 *
 * @param price - The price to be formatted
 * @param fractionDigits - The number of digits after the decimal point. Defaults to 2.
 */
export const toFixed = (price: number | string, fractionDigits: 0| 2 | 3 = 2): string => {
  // If the price is a string convert it to a number
  if (typeof price === "string") {
    price = parseFloat(price);
  }

  return price.toFixed(fractionDigits);
};

export const totalPrice = (line: IOrderLine | IListLine): number => {
  if (!line.totalPrice) {
    return 0;
  }

  return line.totalPrice;
};

export const totalPriceWithVAT = (line: IOrderLine | IListLine): number => {
  if (!line.totalPrice) {
    return 0;
  }

  if (line.vatPercentage === null) {
    return line.totalPrice || 0;
  } else {
    return (line.totalPrice || 0) + (line.totalVatValue || 0);
  }
};

export const unitPriceWithVAT = (line: IOrderLine | IListLine): number => {
  if (!line.price) {
    return 0;
  }

  const unitPrice = line.price;

  if (line.vatPercentage === null || line.vatPercentage === undefined) {
    return unitPrice;
  } else {
    return unitPrice + percentageToPrice(unitPrice, line.vatPercentage);
  }
};

export const unitPrice = (line: IOrderLine | IListLine): number => {
  if (!line.price) {
    return 0;
  }

  const unitPrice = line.price;

  return unitPrice;
};

export const totalPriceVATOnly = (line: IOrderLine | IListLine): number => {
  if (typeof line.totalVatValue === "number") {
    return line.totalVatValue;
  }

  return 0;
};

export const unitPriceVATOnly = (line: IOrderLine | IListLine): number => {
  if (!line.price || !line.vatPercentage) {
    return 0;
  }

  const unitPrice = line.price;

  return percentageToPrice(unitPrice, line.vatPercentage);
};
/**
 * Take a price and return the percentage price of that e.g VAT 20% of a price £1.00 -> £0.20
 *
 * @param price - The original price
 * @param percentage - The percentage you want to return
 */
export const percentageToPrice = (price: number, percentage: number): number => {
  // If the price is a string convert it to a number
  if (typeof price === "string") {
    price = parseFloat(price);
  }

  return (price * percentage) / 100;
};

/**
 *  Formats a price attribute onto an orderline
 * to make sure its using the most upto date values
 *
 * @return IOrderLine
 */
export const formatOrderLineFromPrice = (
  price: IPrice,
  orderLine: Pick<IOrderLine, "productId" | "quantity" | "product">,
  discount = 0
): IOrderLine | IListLine => {
  let prcieToUse = price.price;

  if (discount) {
    prcieToUse = percentageToPrice(price.price, 100 - discount);
  }

  const totalPrice = parseFloat((prcieToUse * orderLine.quantity).toFixed(2));

  return {
    ...orderLine,
    price: prcieToUse,
    totalPrice,
    vatPercentage: price.vatPercentage,
    totalVatValue: price.vatPercentage ? parseFloat(percentageToPrice(totalPrice, price.vatPercentage).toFixed(2)) : null
  };
};

/**
 *  Formats a price attribute onto an orderline
 * to make sure its using the most upto date values
 *
 * @return IOrderLine
 */
export const recalculateOrderLine = (orderLine: IOrderLine | IListLine): IOrderLine | IListLine => {
  let totalPrice = (orderLine.price || 0) * orderLine.quantity;
  let totalVatValue = percentageToPrice(totalPrice, orderLine.vatPercentage || 0);

  if (orderLine.product && orderLine.product.prices) {
    const groupedPrices = _.groupBy(orderLine.product.prices, "minQuantity");
    const selectedPrice = getCurrentPrice(groupedPrices, orderLine.quantity);

    if (selectedPrice) {
      totalPrice = (selectedPrice.price || 0) * orderLine.quantity;
      totalVatValue = percentageToPrice(totalPrice, selectedPrice.vatPercentage || 0);
    }
  }

  return {
    ...orderLine,
    totalPrice,
    totalVatValue
  };
};

export const getCurrentPrice = (groupedPrices: { [key: string]: IPrice[] }, quantity?: number) => {
  let selectedPrice = null;

  if (groupedPrices) {
    const bands = _.keys(groupedPrices);
    const quant = quantity || 1;

    for (const band of bands) {
      const minQuantity = typeof groupedPrices[ band ][ 0 ].minQuantity === "number" ? groupedPrices[ band ][ 0 ].minQuantity : 0;

      if (quant >= (minQuantity as number)) {
        selectedPrice = groupedPrices[ band ][ 0 ];
      }
    }
  }

  return selectedPrice;
};

export const priceWithCommas = (x: string) => {
  const parts = x.toString().split(".");

  parts[ 0 ] = parts[ 0 ].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return parts.join(".");
};

/**
 * Takes a value and returns a string containing a currency
 * rounded down to the nearest 1000 if over 10000
 * @param value
 * @returns string
 */
export const formatShortPrice = (value: number, currency = "GBP"): string => {
  return formatPrice(value, 2, currency, "compact");
};
/**
 * converts a decimal number e.g 1.00 to a number of pence e.g 100
 * @param decimalNumber
 * @returns
 */
export const priceInPence = (decimalNumber: number): number => {
  return parseFloat((decimalNumber * 100).toFixed(2));
};

/**
 * returns a price based on a given discount type and discount value to a given order/list line price
 * @param originalPrice number
 * @param discountTypeAction DiscountTypeAction | undefined
 * @param discount number | undefined
 * @returns price as number
 */
export const appliedDiscountTypePrice = (
  originalPrice: number,
  discount?: number
): number => {
  if (!discount) {
    return originalPrice;
  }

  return originalPrice - ((originalPrice * discount) / 100);
};
